/**
 * Saying goodbye to Denker in live mode: a whole turn that is only a
 * farewell, in English or German (Whisper picks the language itself). It
 * ends live voice like the toggle, and with the wake word on the next
 * "Hey Denker" brings it back. A sentence that merely contains "bye" is a
 * prompt, never a farewell. "Stop listening" is not one: it turns live mode
 * and the wake word off. Push-to-talk keeps `detectVoiceControlIntent`,
 * where a lone "bye" is sent.
 */
import {
  CONTROL_PHRASE_MAX_LENGTH,
  DENKER_NAME_VARIANTS,
  detectVoiceControlIntent,
  normalizeIntentText,
  type VoiceControlMatch,
} from "./voice-control-intents";

export type FarewellLanguage = "en" | "de";

const FAREWELL_PHRASES: Record<FarewellLanguage, string[]> = {
  en: [
    "bye",
    "bye bye",
    "goodbye",
    "good bye",
    "go to sleep",
    "sleep now",
    "that's all bye",
    "that's all goodbye",
  ],
  de: [
    "tschüss",
    "tschüs",
    "tschüß",
    "tschau",
    "ciao",
    "auf wiedersehen",
    "gute nacht",
    "geh schlafen",
    "schlaf jetzt",
  ],
};

const FAREWELL_LANGUAGE = new Map(
  Object.entries(FAREWELL_PHRASES).flatMap(([language, phrases]) =>
    phrases.map((phrase) => [phrase, language as FarewellLanguage] as const),
  ),
);

/** Words said around a farewell that leave it a farewell. */
const FILLER = `(?:${[
  "ok",
  "okay",
  "alright",
  "all right",
  "thanks",
  "thank you",
  "danke",
  ...DENKER_NAME_VARIANTS.flatMap((name) => [name, `hey ${name}`]),
].join("|")})`;
/** Any fillers, the farewell itself, any fillers. The lazy middle leaves
 *  every trailing filler to the tail. */
const FAREWELL_SHAPE = new RegExp(`^(?:${FILLER} )*(.+?)(?: ${FILLER})*$`);

export type LiveVoiceControlMatch = VoiceControlMatch & {
  /** The language a farewell was said in. */
  language?: FarewellLanguage;
};

function farewellLanguage(normalized: string): FarewellLanguage | null {
  if (normalized.length > CONTROL_PHRASE_MAX_LENGTH) return null;
  const phrase = FAREWELL_SHAPE.exec(normalized)?.[1] ?? "";
  return FAREWELL_LANGUAGE.get(phrase) ?? null;
}

/** A live-mode control: a farewell goes to sleep, before any other match. */
export function detectLiveVoiceControlIntent(
  text: string,
): LiveVoiceControlMatch | null {
  const normalized = normalizeIntentText(text);
  const language = farewellLanguage(normalized);
  return language
    ? { intent: "sleep_listening", phrase: normalized, language }
    : detectVoiceControlIntent(text);
}
